/**
 * The v1 rail's expand / collapse control.
 *
 * v1 draws navigation as a 56px icon rail whose groups open as hover flyouts. This adds its
 * second state: pinned open at 236px, every entry labelled, each group's children listed
 * underneath it as a real Bootstrap collapse. `data-nav-expanded="true"` on <html> is the whole
 * switch — see the "V1: the rail PINNED OPEN" block in theme-core.css — and
 * Common/Theming/NavExpanded.cs stamps it during render so the rail is never briefly the wrong
 * width.
 *
 * ⚠ The control is the TOP-BAR TOGGLER, not a new button. Below 1200px `#s-sidebar-toggler` is
 * Serene's hamburger and common-theme.js opens the off-canvas drawer with it; at 1200px and up
 * that class does nothing, because the translate lives inside the sub-1200px media block — so
 * the button was a control that visibly did nothing when pressed. This gives it the meaning it
 * already looks like it has, at the width where it had none.
 *
 * ⚠ Nothing here reloads, unlike the design-generation switch. Pinning is only a matter of
 * which CSS block matches, so re-stamping the attribute completes it in place.
 */

const ATTRIBUTE = "data-nav-expanded";

/**
 * Where the rail stops being a rail and becomes an off-canvas drawer.
 *
 * ⚠ The one number in this file that is duplicated from CSS: theme-core.css fences the pinned
 * block to `min-width: 1200px` and Serene's own drawer rules to `max-width: 1199.98px`. It has
 * to be known here because the decision is behavioural — which of two meanings this button
 * has — and a stylesheet cannot answer that. Kept as a named constant so the pair is findable
 * from either side.
 */
const NAV_BREAKPOINT = 1200;

function isDesktopNav() {
    return window.matchMedia
        ? window.matchMedia(`(min-width: ${NAV_BREAKPOINT}px)`).matches
        : window.innerWidth >= NAV_BREAKPOINT;
}

/** Mirrors Common/Theming/NavExpanded.CookieName — both sides must name the same cookie. */
const COOKIE = "BWDesignSystemNavExpanded";

function isExpanded() {
    return document.documentElement?.getAttribute(ATTRIBUTE) === "true";
}

/**
 * Tells every grid on the page that its canvas changed width.
 *
 * ⚠ Not optional, and not cosmetic. SleekGrid sizes its viewport and its virtual row window in
 * script, and recomputes them on the window's resize event. Pinning the rail moves `.s-main`'s
 * padding — a CSS change, which fires no event — so without this a grid keeps the width it had:
 * the header and the rows disagree about column positions until something else happens to
 * trigger a relayout.
 *
 * Twice, because the width animates over .16s: once now so a grid that reads the container
 * immediately is right, and once after the transition so the final width is the one it keeps.
 * A `transitionend` listener would be exact but fires per property per element and does not
 * fire at all when the transition is interrupted by a second click.
 */
function notifyResize() {
    const fire = () => window.dispatchEvent(new Event("resize"));
    fire();
    setTimeout(fire, 220);
}

/**
 * Gives a clipped nav label a native tooltip, and takes it away again when it is not clipped.
 *
 * ⚠ MEASURED, not guessed. The rail is 236px and the third level is indented twice, so whether
 * a caption fits depends on the caption, the generation's type scale and the viewer's font
 * settings — a length threshold would be wrong on somebody's machine in both directions. An
 * element that overflows reports scrollWidth > clientWidth, which is the actual question.
 *
 * ⚠ Only while PINNED. In the collapsed rail the flyout already names the group and a childless
 * tile has its name in a CSS chip keyed on aria-label, so a native tooltip would arrive a second
 * later and duplicate what is already on screen — which is exactly why the markup does not carry
 * `title` in the first place (see _Sidebar.cshtml).
 *
 * A collapsed group's children measure 0/0 because an ancestor is display:none, so they are
 * correctly left alone until Bootstrap says the group is open.
 */
function syncClippedLabelTitles(expanded: boolean) {
    document.querySelectorAll<HTMLElement>(".s-sidebar-link").forEach(link => {
        const label = expanded
            ? link.querySelector<HTMLElement>(".s-sidebar-link-text")
            : null;

        // The +1 absorbs sub-pixel rounding: a label that fits exactly can report a scrollWidth
        // a fraction larger than its clientWidth and would otherwise get a tooltip repeating a
        // label already fully visible.
        if (label && label.scrollWidth > label.clientWidth + 1)
            link.setAttribute("title", label.textContent?.trim() || "");
        else
            link.removeAttribute("title");
    });
}

function apply(expanded: boolean) {
    const root = document.documentElement;

    if (expanded)
        root.setAttribute(ATTRIBUTE, "true");
    else
        root.removeAttribute(ATTRIBUTE);

    document.cookie = `${COOKIE}=${expanded ? "1" : "0"};path=/;max-age=31536000;samesite=lax`;

    const toggler = document.querySelector<HTMLElement>("#s-sidebar-toggler");
    if (toggler) {
        toggler.setAttribute("aria-expanded", expanded ? "true" : "false");
        // Kept in step with the wording the server renders in _Sidebar.cshtml — the two are the
        // same tooltip and must not drift into two different sentences for the same control.
        toggler.setAttribute("title", expanded ? "Collapse the menu" : "Expand the menu");
    }

    notifyResize();

    // After the .16s width transition, so the measurement is against the width the labels
    // actually end up with rather than the one they are passing through.
    setTimeout(() => syncClippedLabelTitles(expanded), 240);
}

export function installNavExpand() {
    const root = document.documentElement;

    // v2's sidebar has one state — 248px, always labelled — so there is nothing here to toggle.
    // Its copy of the button is hidden at desktop by theme-core.css; this is the same decision
    // on the behaviour side, so a v2 page carries no listener at all.
    if (root?.getAttribute("data-theme-version") === "v2")
        return;

    document.addEventListener("click", e => {
        const toggler = (e.target as HTMLElement)?.closest?.("#s-sidebar-toggler");
        if (!toggler)
            return;

        // Below the breakpoint this button belongs to the drawer, and common-theme.js is
        // already handling this same click. Two behaviours on one button at one width would be
        // a fight; the width decides which one it is.
        if (!isDesktopNav())
            return;

        // ⚠ common-theme.js toggles `body.s-sidebar-expanded` on every click regardless of
        // width. At desktop that class is inert (its transform is inside the sub-1200px block),
        // but leaving it set means a viewport narrowed afterwards would find the drawer already
        // open, over a scrim, with nothing having asked for it. Their listener was registered at
        // DOMContentLoaded and this one after, so this runs second and the removal is the last
        // word.
        document.body.classList.remove("s-sidebar-expanded");

        apply(!isExpanded());
    });

    /**
     * The attribute has to stop describing the rail when the rail stops being a rail.
     *
     * Crossing below the breakpoint turns the sidebar into the off-canvas drawer, which renders
     * labelled and inline for both generations — so "pinned" no longer means anything, and the
     * pinned block is fenced to desktop for exactly that reason. The stored preference is left
     * alone: it is the person's choice about the desktop rail, and coming back above the
     * breakpoint should restore it rather than silently reset it to collapsed.
     */
    if (window.matchMedia) {
        const wide = window.matchMedia(`(min-width: ${NAV_BREAKPOINT}px)`);
        wide.addEventListener?.("change", () => notifyResize());
    }

    // A group's children cannot be measured until they are on screen — Bootstrap says when.
    // ⚠ Bootstrap 5 dispatches this as a native CustomEvent on the collapse element and it
    // bubbles, so one listener on the document covers every group at every level, including
    // groups that did not exist when this ran.
    document.addEventListener("shown.bs.collapse", () => syncClippedLabelTitles(isExpanded()));

    // The server may have stamped the rail open, in which case nothing has been measured yet.
    // ⚠ Deferred a frame: at module-evaluation time the stylesheet may not have applied, and a
    // label measured against an unstyled anchor is measured against the wrong width.
    if (isExpanded())
        requestAnimationFrame(() => syncClippedLabelTitles(true));
}
